import type { KeywordRow, KeywordTier, CompetitionLevel } from "@shared";

/**
 * The Simple view is a lossy projection of a rich KeywordRow — tier + plain
 * commentary + the headline volume, nothing the rich row doesn't already have.
 * Expert/Simple is a view toggle over the SAME artifact; this never changes what
 * gets approved. The thresholds and copy below are the whole "algorithm" the
 * Simple view runs, calibrated in keywordOpportunity.test.ts against a real set.
 */
export interface SimpleKeywordRow {
  id: string;
  tier: KeywordTier;
  title: string;
  volumePhrase: string;
  /** Undefined when we have no competition level to describe. */
  competitionPhrase?: string;
  searchVolume: number;
}

/** Tier off the Launch10 Score (0–100). The score already blends volume ×
 *  affordability × intent, so this single bucket reproduces "high-volume but
 *  informational → Niche" for free. Relative score → tune vs real batches. */
export function tierForScore(launch10Score: number): KeywordTier {
  if (launch10Score >= 65) return "top_pick";
  if (launch10Score >= 35) return "solid";
  return "niche";
}

export function volumePhrase(searchVolume: number): string {
  if (searchVolume >= 70_000) return "Lots of monthly searches";
  if (searchVolume >= 30_000) return "Popular search";
  if (searchVolume >= 15_000) return "Steady interest";
  return "Smaller, focused audience";
}

// Honest for PAID: this is ad-auction competition (who else is bidding), not
// organic ranking difficulty — so no "to rank" language.
const COMPETITION_PHRASES: Record<CompetitionLevel, string> = {
  high: "lots of advertisers bidding",
  medium: "some advertisers bidding",
  low: "few advertisers bidding",
};

export function competitionPhrase(level: CompetitionLevel | undefined): string | undefined {
  return level ? COMPETITION_PHRASES[level] : undefined;
}

export function toSimpleRow(row: KeywordRow): SimpleKeywordRow {
  return {
    id: row.id,
    tier: tierForScore(row.launch10Score),
    title: row.keyword,
    volumePhrase: volumePhrase(row.searchVolume),
    competitionPhrase: competitionPhrase(row.competitionLevel),
    searchVolume: row.searchVolume,
  };
}
